import type { OutputFormat, ProcessingOptions } from "../types";
import { extensionFor, replaceExtension } from "./format";

export function calculateTargetSize(sourceWidth: number, sourceHeight: number, options: ProcessingOptions) {
  if (!options.resize.enabled) {
    return { width: sourceWidth, height: sourceHeight };
  }

  const width = positiveInteger(options.resize.width);
  const height = positiveInteger(options.resize.height);

  if (!width && !height) return { width: sourceWidth, height: sourceHeight };

  let target: { width: number; height: number };

  if (options.resize.maintainAspectRatio) {
    if (width && !height) {
      target = { width, height: Math.max(1, Math.round((width / sourceWidth) * sourceHeight)) };
      return clampUpscale(sourceWidth, sourceHeight, target, options.resize.onlyShrink);
    }

    if (height && !width) {
      target = { width: Math.max(1, Math.round((height / sourceHeight) * sourceWidth)), height };
      return clampUpscale(sourceWidth, sourceHeight, target, options.resize.onlyShrink);
    }

    const scale = Math.min(width! / sourceWidth, height! / sourceHeight);
    target = {
      width: Math.max(1, Math.round(sourceWidth * scale)),
      height: Math.max(1, Math.round(sourceHeight * scale))
    };
    return clampUpscale(sourceWidth, sourceHeight, target, options.resize.onlyShrink);
  }

  target = {
    width: width || sourceWidth,
    height: height || sourceHeight
  };
  return clampUpscale(sourceWidth, sourceHeight, target, options.resize.onlyShrink);
}

export function assertReasonableSize(width: number, height: number) {
  const megapixels = (width * height) / 1_000_000;
  if (megapixels > 80) {
    throw new Error("This image is too large to process safely in the browser. Try resizing it below 80 megapixels first.");
  }
}

export function qualityFor(format: OutputFormat, quality: number) {
  if (format === "png" || format === "bmp" || format === "tiff") return undefined;
  return Math.min(1, Math.max(0.01, quality / 100));
}

export function outputFilename(sourceName: string, options: ProcessingOptions) {
  return replaceExtension(sourceName || `image.${extensionFor(options.format)}`, options.format, options.suffix);
}

function positiveInteger(value?: number) {
  if (!value || value <= 0) return undefined;
  return Math.round(value);
}

function clampUpscale(sourceWidth: number, sourceHeight: number, target: { width: number; height: number }, onlyShrink: boolean) {
  if (!onlyShrink) return target;
  if (target.width <= sourceWidth && target.height <= sourceHeight) return target;
  return { width: sourceWidth, height: sourceHeight };
}
